import { getDaysUntilDate } from "Utils/DateUtils";

/** Number of days before expiration at which a warning should be shown. */
export const EXPIRATION_WARNING_DAYS = 30;

export type DocumentExpirationStatus = {
  isExpired: boolean;
  isExpiringSoon: boolean;
  daysUntilExpiry: number;
};

export const getDocumentExpirationStatus = (
  validTo: string | null,
  isForceExpired = false,
): DocumentExpirationStatus => {
  const daysUntilExpiry = getDaysUntilDate(validTo);
  const isExpired =
    isForceExpired || (validTo !== null && daysUntilExpiry <= 0);
  const isExpiringSoon =
    !isExpired &&
    daysUntilExpiry > 0 &&
    daysUntilExpiry <= EXPIRATION_WARNING_DAYS;

  return { isExpired, isExpiringSoon, daysUntilExpiry };
};

export type DocumentExpirationSeverity =
  | "valid"
  | "expiringSoon"
  | "expired"
  | "missing";

/**
 * Reduces several document valid-to dates into the single most severe status,
 * so a party with multiple documents reflects its soonest-expiring one.
 * Expired outranks expiring-soon, which outranks valid.
 *
 * When none of the supplied documents have a valid-to date, the result is
 * `missing`: an uploaded document with no recorded validity is distinct from
 * one that has genuinely expired. Callers should only invoke this for parties
 * that actually have documents (e.g. gated by `hasIdentificationsUploaded`).
 */
export const getWorstDocumentExpirationSeverity = (
  validToDates: Array<string | null | undefined>,
): DocumentExpirationSeverity => {
  const statuses = validToDates
    .filter((validTo): validTo is string => Boolean(validTo))
    .map((validTo) => getDocumentExpirationStatus(validTo));

  if (statuses.length === 0) {
    return "missing";
  }

  if (statuses.some(({ isExpired }) => isExpired)) {
    return "expired";
  }

  if (statuses.some(({ isExpiringSoon }) => isExpiringSoon)) {
    return "expiringSoon";
  }

  return "valid";
};
